import { useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MoreHorizontal, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AuthImg } from "@/components/auth-img";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { localizeError } from "@/lib/errorMessages";
import {
  useDeletePropertyImage,
  useReorderPropertyImages,
} from "@/features/property/api";
import type { PropertyImage } from "@/types/property";

interface PropertyImageGridProps {
  propertyId: number;
  images: PropertyImage[];
}

/**
 * Drag-to-reorder grid of property images.
 *
 * - Pointer drag-and-drop on desktop (uses the same sensor as the lead
 *   kanban).
 * - Long-press (200ms) + drag on mobile — matches the iOS / Android
 *   gallery convention so it doesn't fight scrolling.
 * - Drop fires a SINGLE {@code PATCH /images/order} so a 20-image
 *   reshuffle is one HTTP call. Optimistic; reverts on error.
 */
export function PropertyImageGrid({ propertyId, images }: PropertyImageGridProps) {
  const reorderMutation = useReorderPropertyImages(propertyId);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  const orderedIds = useMemo(() => images.map((img) => `img-${img.id}`), [images]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIdx = images.findIndex((img) => `img-${img.id}` === active.id);
    const toIdx = images.findIndex((img) => `img-${img.id}` === over.id);
    if (fromIdx === -1 || toIdx === -1) return;
    const reordered = arrayMove(images, fromIdx, toIdx);
    const ids = reordered.map((img) => img.id);
    reorderMutation.mutate(ids, {
      onError: (error) => toast.error(localizeError(error)),
    });
  };

  if (images.length === 0) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={orderedIds} strategy={rectSortingStrategy}>
        {/* data-no-swipe: the wizard's mobile swipe-to-change-step
            handler ignores pointer gestures originating inside this
            grid, so drag-to-reorder isn't hijacked. */}
        <ul
          data-no-swipe
          aria-label="Danh sách ảnh"
          className={cn(
            "grid grid-cols-3 gap-2",
            "sm:grid-cols-4 sm:gap-3",
            "md:grid-cols-6 md:gap-3",
          )}
        >
          {images.map((image, index) => (
            <SortableImage
              key={image.id}
              propertyId={propertyId}
              image={image}
              index={index}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

interface SortableImageProps {
  propertyId: number;
  image: PropertyImage;
  index: number;
}

function SortableImage({ propertyId, image, index }: SortableImageProps) {
  const deleteMutation = useDeletePropertyImage(propertyId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const sortable = useSortable({
    id: `img-${image.id}`,
    data: { type: "image", image },
  });
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } =
    sortable;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative aspect-[4/3] touch-none select-none overflow-hidden rounded-md border bg-muted",
        isDragging ? "z-50 opacity-60 shadow-lg" : "",
      )}
      data-testid={`property-image-${image.id}`}
    >
      <AuthImg
        src={image.thumbnailUrl}
        alt={image.originalFilename}
        loading="lazy"
        draggable={false}
        aria-label={`Kéo để sắp xếp ảnh ${index + 1}`}
        className="h-full w-full cursor-grab object-cover active:cursor-grabbing"
        {...attributes}
        {...listeners}
      />

      {/* Display order badge */}
      <div
        aria-label={`Vị trí ${index + 1}`}
        className="absolute top-1 left-1 flex size-6 items-center justify-center rounded-full bg-black/60 text-[11px] font-semibold text-white"
      >
        {index + 1}
      </div>

      {/* Menu — always visible on mobile, on hover only on desktop. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label={`Tùy chọn ảnh ${index + 1}`}
            className="absolute top-1 right-1 size-11 rounded-full bg-background/80 opacity-100 backdrop-blur-sm md:size-7 md:opacity-0 md:group-hover:opacity-100"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal aria-hidden="true" className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-32">
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => setConfirmOpen(true)}
          >
            <Trash2 aria-hidden="true" className="size-4" />
            Xóa
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xóa ảnh này?</AlertDialogTitle>
            <AlertDialogDescription>
              Ảnh sẽ bị xóa khỏi bất động sản. Bạn có thể tải lại ảnh khác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(image.id, {
                  onError: (error) => toast.error(localizeError(error)),
                });
                setConfirmOpen(false);
              }}
            >
              Xóa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}
